<template>
  <ModalLayout small title="Archive">
    <div class="rounded-t-[10px] bg-white px-[24px] pt-[41px] sm:px-[40px]">
      <div
        class="absolute right-[24px] top-[36px] flex h-[32px] w-[32px] cursor-pointer items-center justify-center rounded-full border border-gpx_gray-800 sm:right-[40px]"
        @click.prevent="close"
      >
        <XMarkIcon
          class="heroicon-stroke-w-1.4 h-[20px] w-[20px] text-gpx_gray-200"
          @click="close"
        />
      </div>
      <div class="border-b border-b-gpx_gray-800 pb-[18px]">
        <div class="text-gpx_black-100 text-2xl font-semibold">
          {{ params.title }}
        </div>
      </div>
      <div class="mt-[25px] text-sm text-gpx_gray-100">
        <p class="mb-2">These devices will be:</p>
        <ul class="list-disc pl-5 space-y-1 mb-2">
          <li>Removed from your tracker list and map</li>
          <li>Excluded from Scout queries and alerts</li>
          <li>Recoverable for 10 days, then permanently archived</li>
        </ul>
        <p>Historical data remains accessible per your plan.</p>
      </div>
      <div class="mt-4">
        <label class="mb-2 block text-sm font-medium text-gpx_gray-200">
          Type "Archive" to confirm
        </label>
        <input
          ref="confirmInputRef"
          v-model="confirmText"
          type="text"
          class="form-input w-full rounded border border-gpx_gray-800 px-3 py-2 text-sm"
          placeholder="Archive"
          autocomplete="off"
          @keydown.enter="isConfirmEnabled && handleConfirm()"
        />
      </div>
    </div>
    <div
      class="flex w-full gap-x-[16px] rounded-b-[10px] bg-white px-[24px] py-[32px] sm:px-[40px]"
    >
      <Button class="w-full" @click="close">
        Cancel
      </Button>
      <Button
        variant="destructive"
        class="w-full"
        :disabled="!isConfirmEnabled"
        :loading="isLoading"
        @click="handleConfirm"
      >
        Archive
      </Button>
    </div>
  </ModalLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue';
import { useToast } from 'vue-toastification';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import ModalLayout from '@/layouts/ModalLayout.vue';

const CONFIRM_WORD = 'archive';

const props = defineProps<{
  params: {
    title: string;
    body?: string;
    onConfirm: () => Promise<void>;
    successMessage?: string;
    errorMessage?: string;
  };
}>();

const emit = defineEmits<{ (e: 'close'): void }>();

const toast = useToast();
const confirmText = ref('');
const confirmInputRef = ref<HTMLInputElement | null>(null);
const isLoading = ref(false);

const isConfirmEnabled = computed(
  () => confirmText.value.trim().toLowerCase() === CONFIRM_WORD
);

function close() {
  emit('close');
}

function handleConfirm() {
  if (!isConfirmEnabled.value || isLoading.value) return;
  isLoading.value = true;
  props.params
    .onConfirm()
    .then(() => {
      close();
      toast.success(props.params.successMessage ?? 'Archived successfully');
    })
    .catch(() => {
      toast.error(props.params.errorMessage ?? 'An error occurred');
    })
    .finally(() => {
      isLoading.value = false;
    });
}

onMounted(() => {
  nextTick(() => {
    confirmInputRef.value?.focus();
  });
});
</script>
